<template>
    <div
        ref="rootEl"
        class="relative h-full min-h-0 w-full overflow-hidden font-sans outline-hidden select-none [&:-webkit-full-screen]:h-screen [&:-webkit-full-screen]:w-screen [&:-webkit-full-screen]:bg-black [&:fullscreen]:h-screen [&:fullscreen]:w-screen [&:fullscreen]:bg-black"
        :style="{ '--mp-bg': backgroundColor, background: backgroundColor }"
        tabindex="0"
        @keydown.left.prevent="prev"
        @keydown.right.prevent="next"
        @keydown.esc="exitFullscreen"
    >
        <div
            class="absolute inset-0 overflow-hidden"
            @touchstart.passive="onTouchStart"
            @touchmove.passive="onTouchMove"
            @touchend="onTouchEnd"
        >
            <div
                ref="trackEl"
                class="flex h-full w-full will-change-transform"
                :class="{ '[transition:transform_360ms_cubic-bezier(0.87,0,0.13,1)]': animating }"
                :style="trackStyle"
                @transitionend="onTrackTransitionEnd"
            >
                <div
                    v-for="(file, i) in extendedFiles"
                    :key="slideKey(file, i)"
                    class="flex h-full w-full shrink-0 items-center justify-center"
                    :style="{ background: backgroundColor }"
                >
                    <slot
                        v-if="file.unavailable"
                        name="unavailable"
                        :file="file"
                    />
                    <slot
                        v-else
                        name="slide"
                        v-bind="slideProps(file, i)"
                    >
                        <!-- Only the current slide and its neighbours load, so a long gallery never fetches every file. -->
                        <template v-if="isInWindow(i)">
                            <video
                                v-if="isVideoFile(file)"
                                :src="file.url"
                                controls
                                playsinline
                                class="block h-full w-full object-contain"
                            ></video>
                            <img
                                v-else
                                :src="file.url"
                                :alt="file.name || ''"
                                class="pointer-events-none block h-full w-full object-cover select-none md:object-contain"
                                draggable="false"
                                @error="emit('image-error', realIndex(i))"
                            />
                        </template>
                    </slot>
                </div>
            </div>
        </div>

        <!-- Top-right controls -->
        <div class="absolute top-3 right-3 z-30 flex gap-2">
            <button
                v-if="showMaximize && supportsFullscreen && !isFullscreen"
                type="button"
                class="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-0 bg-black/55 p-0 text-white backdrop-blur-xs transition-colors duration-200 hover:bg-black/78 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f3df0] focus-visible:outline-solid"
                aria-label="Enter fullscreen"
                @click="enterFullscreen"
            >
                <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path d="M4 9V5a1 1 0 0 1 1-1h4" />
                    <path d="M20 9V5a1 1 0 0 0-1-1h-4" />
                    <path d="M4 15v4a1 1 0 0 0 1 1h4" />
                    <path d="M20 15v4a1 1 0 0 1-1 1h-4" />
                </svg>
            </button>
            <button
                v-if="isFullscreen"
                type="button"
                class="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-0 bg-black/55 p-0 text-white backdrop-blur-xs transition-colors duration-200 hover:bg-black/78 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f3df0] focus-visible:outline-solid"
                aria-label="Exit fullscreen"
                @click="exitFullscreen"
            >
                <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7a1 1 0 1 0-1.41 1.41L10.59 12 5.7 16.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z"
                    />
                </svg>
            </button>
        </div>

        <!-- Left arrow -->
        <button
            v-if="showArrows && files.length > 1"
            type="button"
            class="absolute top-1/2 left-2 z-30 inline-flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-black/45 p-0 text-white backdrop-blur-xs transition-colors duration-200 hover:bg-black/72 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f3df0] focus-visible:outline-solid sm:left-3 sm:h-10 sm:w-10"
            aria-label="Previous image"
            @click="prev"
        >
            <svg
                viewBox="0 0 24 24"
                width="22"
                height="22"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M15.41 16.59 10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
            </svg>
        </button>

        <!-- Right arrow -->
        <button
            v-if="showArrows && files.length > 1"
            type="button"
            class="absolute top-1/2 right-2 z-30 inline-flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-black/45 p-0 text-white backdrop-blur-xs transition-colors duration-200 hover:bg-black/72 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f3df0] focus-visible:outline-solid sm:right-3 sm:h-10 sm:w-10"
            aria-label="Next image"
            @click="next"
        >
            <svg
                viewBox="0 0 24 24"
                width="22"
                height="22"
                fill="currentColor"
                aria-hidden="true"
            >
                <path d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z" />
            </svg>
        </button>

        <!-- Counter -->
        <div
            v-if="showCounter && files.length > 1"
            class="absolute bottom-3 left-1/2 z-30 -translate-x-1/2 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium tracking-[0.02em] text-white tabular-nums backdrop-blur-xs"
            aria-live="polite"
        >
            {{ current + 1 }} / {{ files.length }}
        </div>
    </div>
</template>

<script setup lang="ts">
interface CarouselFile {
    id?: string | number;
    url: string;
    name?: string;
    mime_type?: string;
    content_type?: string;
    /** Renders the `unavailable` slot in place of the file. */
    unavailable?: boolean;
}

interface Props {
    files: CarouselFile[];
    showArrows?: boolean;
    showCounter?: boolean;
    showMaximize?: boolean;
    initialIndex?: number;
    backgroundColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
    showArrows: true,
    showCounter: true,
    showMaximize: true,
    initialIndex: 0,
    backgroundColor: '#000',
});

const emit = defineEmits<{ (e: 'update:index', index: number): void; (e: 'image-error', index: number): void }>();

const rootEl = ref<HTMLElement | null>(null);
const trackEl = ref<HTMLElement | null>(null);
const current = ref(0);
const visualIndex = ref(0);
const animating = ref(true);
const isFullscreen = ref(false);
const supportsFullscreen = ref(false);

const isTouching = ref(false);
const touchStartX = ref(0);
const touchDeltaX = ref(0);
const viewportWidth = ref(0);

const transitionDuration = 360;
let pendingVisualResetIndex: number | null = null;
let visualResetFallbackTimeout: ReturnType<typeof setTimeout> | null = null;

const extendedFiles = computed(() =>
    props.files.length > 1 ? [props.files[props.files.length - 1], ...props.files, props.files[0]] : props.files,
);

/** The file a track slide shows, mapping the loop clones at either end to the file they copy. */
const realIndex = (slideIndex: number): number => {
    const count = props.files.length;
    if (count <= 1) return slideIndex;
    if (slideIndex === 0) return count - 1;
    if (slideIndex === count + 1) return 0;
    return slideIndex - 1;
};

/** By file rather than track position, so a clone and its real slide load together and a wrap's snap never lands on an empty slide. */
const isInWindow = (slideIndex: number): boolean => {
    const count = props.files.length;
    const distance = Math.abs(realIndex(slideIndex) - current.value);
    return Math.min(distance, count - distance) <= 1;
};

/** The loop copies at either end of the track; anything heavier than an image should not load twice. */
const isClone = (slideIndex: number): boolean =>
    props.files.length > 1 && (slideIndex === 0 || slideIndex === props.files.length + 1);

/** By file, so a list that changes around the slide on screen keeps its element: a playing video or framed PDF isn't remounted. */
const slideKey = (file: CarouselFile, slideIndex: number): string => {
    if (isClone(slideIndex)) return slideIndex === 0 ? 'clone-start' : 'clone-end';
    return file.id != null ? `file-${file.id}` : `index-${slideIndex}`;
};

/** What a caller's `slide` slot gets to decide how much of a slide to render. */
const slideProps = (file: CarouselFile, slideIndex: number) => ({
    file,
    index: realIndex(slideIndex),
    isCurrent: !isClone(slideIndex) && realIndex(slideIndex) === current.value,
    isClone: isClone(slideIndex),
    inWindow: isInWindow(slideIndex),
});

const isVideoFile = (file: CarouselFile): boolean =>
    String(file.mime_type ?? file.content_type ?? '')
        .toLowerCase()
        .startsWith('video/');

const trackStyle = computed(() => {
    const base = -visualIndex.value * 100;
    const dragPct = isTouching.value && viewportWidth.value ? (touchDeltaX.value / viewportWidth.value) * 100 : 0;
    return { transform: `translate3d(${base + dragPct}%, 0, 0)` };
});

const clearVisualResetFallback = () => {
    if (!visualResetFallbackTimeout) return;
    clearTimeout(visualResetFallbackTimeout);
    visualResetFallbackTimeout = null;
};

const setCurrentIndex = (index: number) => {
    const max = Math.max(0, props.files.length - 1);
    current.value = Math.min(Math.max(0, index), max);
    visualIndex.value = props.files.length > 1 ? current.value + 1 : current.value;
};

// Before the first render, so the load window opens at the starting file rather than file 0.
setCurrentIndex(props.initialIndex);

const reEnableAnimation = () => {
    animating.value = true;
};

/** Turns the slide animation back on once a snap without it has painted. */
const reEnableAnimationAfterSnap = () => {
    nextTick(() => {
        void trackEl.value?.offsetWidth;
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(reEnableAnimation);
        else setTimeout(reEnableAnimation, 0);
    });
};

const completeVisualReset = () => {
    if (pendingVisualResetIndex === null) return;
    const resetIndex = pendingVisualResetIndex;
    pendingVisualResetIndex = null;
    clearVisualResetFallback();

    animating.value = false;
    visualIndex.value = resetIndex;

    reEnableAnimationAfterSnap();
};

const clearPendingVisualReset = (settle = false) => {
    if (pendingVisualResetIndex === null) return;
    const resetIndex = pendingVisualResetIndex;
    pendingVisualResetIndex = null;
    clearVisualResetFallback();

    if (settle) {
        animating.value = false;
        visualIndex.value = resetIndex;
    }
};

const queueVisualReset = (index: number) => {
    clearPendingVisualReset();
    pendingVisualResetIndex = index;
    visualResetFallbackTimeout = setTimeout(completeVisualReset, transitionDuration + 80);
};

const prepareForNavigation = () => {
    clearPendingVisualReset(true);
    animating.value = true;
};

const onTrackTransitionEnd = (e: TransitionEvent) => {
    if (e.target !== trackEl.value || e.propertyName !== 'transform') return;
    completeVisualReset();
};

// A single file has no loop copies to page onto, so paging would push it off-screen.
const next = () => {
    if (props.files.length <= 1) return;
    prepareForNavigation();
    visualIndex.value += 1;
    current.value = (current.value + 1) % props.files.length;
    emit('update:index', current.value);
    if (props.files.length > 1 && visualIndex.value === props.files.length + 1) {
        queueVisualReset(1);
    }
};

const prev = () => {
    if (props.files.length <= 1) return;
    prepareForNavigation();
    visualIndex.value -= 1;
    current.value = (current.value - 1 + props.files.length) % props.files.length;
    emit('update:index', current.value);
    if (props.files.length > 1 && visualIndex.value === 0) {
        queueVisualReset(props.files.length);
    }
};

const goTo = (i: number) => {
    if (i < 0 || i >= props.files.length) return;
    prepareForNavigation();
    current.value = i;
    visualIndex.value = props.files.length > 1 ? i + 1 : i;
    emit('update:index', current.value);
};

/** Moves to a slide without animating, so the file on screen stays put. */
const jumpTo = (i: number) => {
    clearPendingVisualReset();
    animating.value = false;
    setCurrentIndex(i);
    emit('update:index', current.value);
    reEnableAnimationAfterSnap();
};

// A list that changes while open (a refetch adding files above) keeps the same file on screen, not the same position.
watch(
    () => props.files,
    (files, previous) => {
        const id = previous[current.value]?.id;
        if (id === undefined || !files.length) return;
        const found = files.findIndex((file) => file.id === id);
        const index = found === -1 ? Math.min(current.value, files.length - 1) : found;
        // Crossing one file adds or removes the loop copies, which moves the slide on screen even at the same index.
        if (index !== current.value || previous.length > 1 !== files.length > 1) jumpTo(index);
    },
);

const onTouchStart = (e: TouchEvent) => {
    // Scrubbing a video's seek bar must not drag the track.
    if (!e.touches?.length || (e.target as Element | null)?.closest?.('video')) return;
    clearPendingVisualReset(true);
    isTouching.value = true;
    animating.value = false;
    touchStartX.value = e.touches[0].clientX;
    touchDeltaX.value = 0;
    viewportWidth.value = rootEl.value?.clientWidth ?? 1;
};

const onTouchMove = (e: TouchEvent) => {
    if (!isTouching.value || !e.touches?.length) return;
    touchDeltaX.value = e.touches[0].clientX - touchStartX.value;
};

const onTouchEnd = () => {
    if (!isTouching.value) return;
    const threshold = Math.max(40, viewportWidth.value * 0.15);
    animating.value = true;
    if (touchDeltaX.value <= -threshold) next();
    else if (touchDeltaX.value >= threshold) prev();
    isTouching.value = false;
    touchDeltaX.value = 0;
};

const enterFullscreen = async () => {
    const el = rootEl.value;
    if (!el) return;
    try {
        if (el.requestFullscreen) await el.requestFullscreen();
        else if ((el as any).webkitRequestFullscreen) (el as any).webkitRequestFullscreen();
    } catch {
        isFullscreen.value = true;
    }
    await nextTick();
    el.focus({ preventScroll: true });
};

const exitFullscreen = async () => {
    try {
        if (document.fullscreenElement && document.exitFullscreen) {
            await document.exitFullscreen();
        } else if ((document as any).webkitFullscreenElement && (document as any).webkitExitFullscreen) {
            (document as any).webkitExitFullscreen();
        }
    } catch {
        /* ignore */
    }
    isFullscreen.value = false;
};

const onFullscreenChange = () => {
    const fsEl = document.fullscreenElement || (document as any).webkitFullscreenElement;
    isFullscreen.value = fsEl === rootEl.value;
};

onMounted(() => {
    supportsFullscreen.value = !!(document.fullscreenEnabled || (document as any).webkitFullscreenEnabled);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
});

onBeforeUnmount(() => {
    clearPendingVisualReset();
    document.removeEventListener('fullscreenchange', onFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
});

defineExpose({ next, prev, goTo, current });
</script>
